import { useCallback, useState } from 'react';

import type { AnswerKey, NodeId } from '../engine';
import type { QuestionView } from '../model/view';

interface OpenState {
    nodeId: NodeId | null;
    keys: ReadonlySet<AnswerKey>;
}

export interface OpenSharedQuestions {
    /** Toont het onderdeel deze gedeelde vraag als volledige kaart? */
    isOpen: (key: AnswerKey) => boolean;
    open: (key: AnswerKey) => void;
    close: (key: AnswerKey) => void;
}

/**
 * Welke gedeelde vragen een hoofdonderdeel als volledige kaart toont in plaats van
 * als regel met het antwoord van elders. Alleen vragen die in een *ander*
 * hoofdonderdeel horen komen hier langs; binnen één stroom staat de vraag al boven
 * in beeld en wordt hij niet herhaald.
 *
 * - Staat zo'n vraag nog open, dan stelt dit onderdeel hem gewoon: de inspecteur
 *   hoeft er niet voor terug.
 * - Wie hem daarna beantwoordt, houdt de kaart tot hij het onderdeel verlaat of
 *   "Klaar" tikt. Anders klapt de kaart onder de vinger in en schuift alles
 *   eronder omhoog, precies op het moment van de volgende tik.
 * - Een vraag die elders al beantwoord is, komt hier niet meer voorbij: het
 *   antwoord staat er, en het onderdeel waar hij hoort is de plek om hem te
 *   wijzigen.
 *
 * Dit is het "state aanpassen tijdens render"-patroon (zie `useDraftText`): een
 * effect zou eerst de ingeklapte regel laten zien en dan pas de kaart.
 */
export function useOpenSharedQuestions(
    nodeId: NodeId | null,
    questions: readonly QuestionView[],
): OpenSharedQuestions {
    const [state, setState] = useState<OpenState>(() => ({ nodeId, keys: new Set() }));
    let current = state.nodeId === nodeId ? state : { nodeId, keys: new Set<AnswerKey>() };
    const unanswered = questions.filter(
        (view) => view.askedIn !== null && !view.answered && !current.keys.has(view.key),
    );

    if (unanswered.length > 0) {
        current = {
            nodeId,
            keys: new Set([...current.keys, ...unanswered.map((view) => view.key)]),
        };
    }

    if (current !== state) {
        setState(current);
    }

    const keys = current.keys;

    const open = useCallback(
        (key: AnswerKey) =>
            setState((previous) => ({
                nodeId: previous.nodeId,
                keys: new Set([...previous.keys, key]),
            })),
        [],
    );

    const close = useCallback(
        (key: AnswerKey) =>
            setState((previous) => {
                const next = new Set(previous.keys);

                next.delete(key);

                return { nodeId: previous.nodeId, keys: next };
            }),
        [],
    );

    return { isOpen: (key) => keys.has(key), open, close };
}
